import { Component, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, RouterLinkActive, RouterLink } from '@angular/router';
import { ModalRef } from 'src/app/shared/utils/modal/modal-ref';
import { ModalService } from 'src/app/shared/utils/modal/modal.service';
import { LanguageUtilService } from 'src/app/services/language-util.service';
import { SessionService } from 'src/app/services/session.service';
import { StorageService } from 'src/app/services/storage.service';
import { Commons } from 'src/app/shared/utils/commons';
import { SelectLanguageModalComponent } from 'src/app/shared/utils/modals/select-language-modal/select-language-modal.component';
import { DropdownDirective, DropdownToggleDirective, DropdownMenuDirective } from '../../shared/utils/directives/dropdown.directive';
import { TranslocoPipe } from '@ngneat/transloco';

declare interface RouteInfo {
  path: string;
  title: string;
  icon: string;
  class: string;
}

export const MENU_ROUTES: RouteInfo[] = [
  { path: '/' + Commons.PATH_MAIN, title: 'label.store', icon: 'sdx-bag text-primary', class: '' },
  { path: '/' + Commons.PATH_PURCHASE, title: 'label.payments', icon: 'sdx sdx-money-bill text-yellow', class: '' },
  { path: '/' + Commons.PATH_UF_CONVERTER, title: 'label.uf-converter', icon: 'sdx sdx-exchange text-info', class: '' },
];

export const PUBLIC_ROUTES: RouteInfo[] = [
  { path: '/' + Commons.PATH_MY_COMPANY, title: 'label.organization', icon: 'sdx-building text-red', class: '' },
  { path: '/' + Commons.PATH_MY_LICENCES, title: 'label.subscriptions', icon: 'sdx-basket text-orange', class: '' },
  { path: '/' + Commons.PATH_MY_PURCHASES, title: 'label.payment-history', icon: 'sdx sdx-receipt text-success', class: '' },
];

export const ADMIN_ROUTES: RouteInfo[] = [
  { path: '/' + Commons.PATH_PRODUCTS, title: 'label.products', icon: 'sdx-app text-primary', class: '' },
  { path: '/' + Commons.PATH_PLANS, title: 'label.plans', icon: 'sdx sdx-clipboard-check text-success', class: '' },
  { path: '/' + Commons.PATH_LICENCES, title: 'label.licences', icon: 'sdx-money-coins text-orange', class: '' },
  { path: '/' + Commons.PATH_CUSTOMERS, title: 'label.customers', icon: 'sdx-user text-yellow', class: '' },
  { path: '/' + Commons.PATH_COMPANIES, title: 'label.companies', icon: 'sdx-building text-red', class: '' },
  { path: '/' + Commons.PATH_SALES_REPORT, title: 'label.sales-report', icon: 'sdx sdx-chart-line text-success', class: '' },
  { path: '/' + Commons.PATH_INTEGRATIONS, title: 'label.integrations', icon: 'sdx sdx-cog text-info', class: '' },
];

export const SETTINGS_ROUTES: RouteInfo[] = [
  { path: '/' + Commons.PATH_MY_CUSTOMER, title: 'label.profile', icon: 'sdx-user text-primary', class: '' }
];

export const ALL_ROUTES: RouteInfo[] = MENU_ROUTES.concat(PUBLIC_ROUTES.concat(ADMIN_ROUTES.concat(SETTINGS_ROUTES)))

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  imports: [RouterLinkActive, RouterLink, DropdownDirective, DropdownToggleDirective, DropdownMenuDirective, TranslocoPipe]
})
export class SidebarComponent implements OnInit {

  public menuItems: any[] = [];
  public publicItems: any[] = [];
  public adminItems: any[] = [];
  public settingsItems: any[] = [];
  public isCollapsed = true;

  loginPath: string = Commons.PATH_LOGIN
  profilePath: string = Commons.PATH_MY_CUSTOMER
  supportPath: string = Commons.PATH_SUPPORT
  mainPath: string = Commons.PATH_MAIN

  CLI_EXCLUDED_PATHS = [
    '/' + Commons.PATH_MY_LICENCES,
    '/' + Commons.PATH_MY_PURCHASES
  ]

  APP_EXCLUDED_PATHS = [
    '/' + Commons.PATH_MY_CUSTOMER
  ]

  sessionIsOpen: boolean = Commons.sessionIsOpen()
  sessionObject: any = Commons.sessionObject()
  avatarUrl: any = Commons.DF_AVATAR
  isSuperUser: boolean = Commons.sessionIsSuperUser()
  langIcon: string = './assets/img/languages/LANG.png'
  isAppType: boolean = false;
  isCliType: boolean = false;
  isRWXRol: boolean = false;

  selLangModal: ModalRef<SelectLanguageModalComponent> | null = null;

  constructor(
    private sessionService: SessionService,
    private router: Router,
    private storageService: StorageService,
    private sanitizer: DomSanitizer,
    private langService: LanguageUtilService,
    private modalService: ModalService,
  ) { }

  ngOnInit() {
    this.menuItems = MENU_ROUTES.filter(menuItem => menuItem);
    this.isCliType = this.sessionIsOpen
      && this.sessionObject?.customer?.type === Commons.USER_TYPE_BASIC;

    this.isRWXRol = this.sessionIsOpen
      && this.sessionObject?.customer.rol === Commons.USER_ROL_RWX;

    this.publicItems = PUBLIC_ROUTES.filter(item =>
      !this.CLI_EXCLUDED_PATHS.includes(item.path)
      || (!this.isCliType && this.isRWXRol)
    )
    this.adminItems = ADMIN_ROUTES.filter(adminItem => adminItem);
    this.isAppType = this.sessionIsOpen
      && this.sessionObject?.customer?.type === Commons.USER_TYPE_APP

    this.settingsItems = SETTINGS_ROUTES.filter(settingItem =>
      !this.isAppType || !this.APP_EXCLUDED_PATHS.includes(settingItem.path));
    this.router.events.subscribe((event) => {
      this.isCollapsed = true;
    });
    this.langIcon = this.langIcon.replace('LANG', this.langService.getLangActive())
    if (this.sessionIsOpen && this.sessionObject.customer.avatar !== Commons.DF_AVATAR) {
      this.storageService.getImage(this.sessionObject.customer.avatar).subscribe(
        {
          next: async (v) => {
            this.avatarUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(v))
          },
          error: (e) => {
            this.avatarUrl = this.sanitizer.bypassSecurityTrustUrl(Commons.DF_AVATAR)
          },
          complete: () => { }
        }
      )
    }

  }

  closeSession() {
    if (Commons.sessionIsOpen()) {
      this.sessionService.setUserLoggedIn(false);
      Commons.sessionClose()
      this.router.navigate([Commons.PATH_MAIN]).then(() => {
        window.location.reload();
      });
    }
  }

  openLanguageModal() {
    this.selLangModal = this.modalService.open(SelectLanguageModalComponent)
    this.selLangModal.onClose.subscribe((lang: any) => {
      if (lang !== this.langService.getLangActive()) {
        this.langService.setLanguage(lang)
        window.location.reload();
      }
    });
  }
}
